import { useState } from 'react';
import clsx from 'clsx';
import { BookOpen, Boxes, Compass, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { lessonsPerStage } from '@doit/shared';
import { ConfirmDialog } from '../../components/Modal';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import {
  useCourses,
  useDeleteCourse,
  useDeleteDirection,
  useDeleteFlow,
  useDeleteProject,
  useDirections,
  useFlows,
  useProjects,
} from '../../api/queries';
import type { Course, Direction, Flow, Project } from '../../api/types';
import { ApiError } from '../../api/client';
import { formatDate } from '../../lib/format';
import { CourseModal, DirectionModal, FlowModal, ProjectModal } from './modals';

type Tab = 'projects' | 'flows' | 'directions' | 'courses';

const TABS: { id: Tab; label: string; icon: typeof Layers }[] = [
  { id: 'projects', label: 'პროექტები', icon: Boxes },
  { id: 'flows', label: 'ნაკადები', icon: Layers },
  { id: 'directions', label: 'მიმართულებები', icon: Compass },
  { id: 'courses', label: 'საგნები', icon: BookOpen },
];

export function CatalogPage() {
  const [tab, setTab] = useState<Tab>('projects');

  return (
    <>
      <PageHeader
        title="კატალოგი"
        subtitle="პროექტი → ნაკადი → ჯგუფი; მიმართულებები და საგნები დამოუკიდებლად იმართება"
      />

      <nav className="mb-5 flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-[13px] font-medium transition',
              tab === t.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-mute hover:text-ink',
            )}
          >
            <t.icon className="size-4" />
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'projects' && <ProjectsTab />}
      {tab === 'flows' && <FlowsTab />}
      {tab === 'directions' && <DirectionsTab />}
      {tab === 'courses' && <CoursesTab />}
    </>
  );
}

// ============================================================
// პროექტები
// ============================================================

function ProjectsTab() {
  const { data, isLoading } = useProjects();
  const remove = useDeleteProject();
  const [editing, setEditing] = useState<Project | null>(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Project | null>(null);
  const confirmDelete = useDeleteHandler(remove.mutateAsync, () => setToDelete(null));

  if (isLoading) return <Loading />;

  return (
    <>
      <TableShell
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        addLabel="პროექტის დამატება"
        empty={!data?.length}
        emptyTitle="პროექტები ჯერ არ არის"
        emptyText="პროექტი ყველაფრის სათავეა — DoIT, კორპორატიული დაკვეთა და ა.შ."
        head={
          <tr>
            <Th>დასახელება</Th>
            <Th>კოდი</Th>
            <Th>აღწერა</Th>
            <Th className="text-right">ნაკადი</Th>
            <Th className="text-right">საგანი</Th>
            <Th className="text-right">სტუდენტი</Th>
            <Th>სტატუსი</Th>
            <Th />
          </tr>
        }
      >
        {data?.map((p) => (
          <tr key={p.id} className="border-t border-line hover:bg-canvas/60">
            <Td>
              <span className="flex items-center gap-2.5">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: p.color ?? '#cfd4dc' }}
                />
                <span className="font-medium text-ink">{p.name}</span>
              </span>
            </Td>
            <Td className="tabular text-ink-mute">{p.code}</Td>
            <Td className="max-w-64 truncate text-ink-mute">{p.description ?? '—'}</Td>
            <Td className="tabular text-right">{p._count.flows}</Td>
            <Td className="tabular text-right">{p._count.courses}</Td>
            <Td className="tabular text-right">{p._count.enrollments}</Td>
            <Td>
              <ActiveBadge active={p.isActive} />
            </Td>
            <Td className="text-right">
              <RowActions
                onEdit={() => {
                  setEditing(p);
                  setOpen(true);
                }}
                onDelete={() => setToDelete(p)}
              />
            </Td>
          </tr>
        ))}
      </TableShell>

      <ProjectModal open={open} onClose={() => setOpen(false)} project={editing} />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && void confirmDelete(toDelete.id)}
        title="პროექტის წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.name}</strong>. მოქმედება შეუქცევადია.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================
// ნაკადები
// ============================================================

function FlowsTab() {
  const { data, isLoading } = useFlows();
  const remove = useDeleteFlow();
  const [editing, setEditing] = useState<Flow | null>(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Flow | null>(null);
  const confirmDelete = useDeleteHandler(remove.mutateAsync, () => setToDelete(null));

  if (isLoading) return <Loading />;

  return (
    <>
      <TableShell
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        addLabel="ნაკადის დამატება"
        empty={!data?.length}
        emptyTitle="ნაკადები ჯერ არ არის"
        emptyText="ნაკადი პროექტის მიღების ტალღაა — სახელი პროექტის კოდისგან იგება (DoIT-4)."
        head={
          <tr>
            <Th>დასახელება</Th>
            <Th>პროექტი</Th>
            <Th>ნომერი</Th>
            <Th>პერიოდი</Th>
            <Th className="text-right">ჯგუფი</Th>
            <Th>სტატუსი</Th>
            <Th />
          </tr>
        }
      >
        {data?.map((f) => (
          <tr key={f.id} className="border-t border-line hover:bg-canvas/60">
            <Td className="font-medium text-ink">{f.name}</Td>
            <Td>
              <span className="flex items-center gap-2">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: f.project.color ?? '#cfd4dc' }}
                />
                <span className="text-ink-soft">{f.project.name}</span>
              </span>
            </Td>
            <Td className="tabular text-ink-mute">{f.code}</Td>
            <Td className="text-ink-mute">{periodLabel(f.startDate, f.endDate)}</Td>
            <Td className="tabular text-right">{f._count.groups}</Td>
            <Td>
              <ActiveBadge active={f.isActive} />
            </Td>
            <Td className="text-right">
              <RowActions
                onEdit={() => {
                  setEditing(f);
                  setOpen(true);
                }}
                onDelete={() => setToDelete(f)}
              />
            </Td>
          </tr>
        ))}
      </TableShell>

      <FlowModal open={open} onClose={() => setOpen(false)} flow={editing} />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && void confirmDelete(toDelete.id)}
        title="ნაკადის წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.name}</strong>. მოქმედება შეუქცევადია.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================
// მიმართულებები
// ============================================================

function DirectionsTab() {
  const { data, isLoading } = useDirections();
  const remove = useDeleteDirection();
  const [editing, setEditing] = useState<Direction | null>(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Direction | null>(null);
  const confirmDelete = useDeleteHandler(remove.mutateAsync, () => setToDelete(null));

  if (isLoading) return <Loading />;

  return (
    <>
      <TableShell
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        addLabel="მიმართულების დამატება"
        empty={!data?.length}
        emptyTitle="მიმართულებები ჯერ არ არის"
        emptyText="მაგალითად: პროგრამირება, დიზაინი, მართვა."
        head={
          <tr>
            <Th>დასახელება</Th>
            <Th>კოდი</Th>
            <Th className="text-right">კურსი</Th>
            <Th>სტატუსი</Th>
            <Th />
          </tr>
        }
      >
        {data?.map((d) => (
          <tr key={d.id} className="border-t border-line hover:bg-canvas/60">
            <Td>
              <span className="flex items-center gap-2.5">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: d.color ?? '#cfd4dc' }}
                />
                <span className="font-medium text-ink">{d.name}</span>
              </span>
            </Td>
            <Td className="tabular text-ink-mute">{d.code}</Td>
            <Td className="tabular text-right">{d._count.courses}</Td>
            <Td>
              <ActiveBadge active={d.isActive} />
            </Td>
            <Td className="text-right">
              <RowActions
                onEdit={() => {
                  setEditing(d);
                  setOpen(true);
                }}
                onDelete={() => setToDelete(d)}
              />
            </Td>
          </tr>
        ))}
      </TableShell>

      <DirectionModal open={open} onClose={() => setOpen(false)} direction={editing} />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && void confirmDelete(toDelete.id)}
        title="მიმართულების წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.name}</strong>. მოქმედება შეუქცევადია.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================
// კურსები
// ============================================================

function CoursesTab() {
  const { data, isLoading } = useCourses();
  const { data: directions } = useDirections();
  const { data: projects } = useProjects();
  const remove = useDeleteCourse();
  const [editing, setEditing] = useState<Course | null>(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Course | null>(null);
  const confirmDelete = useDeleteHandler(remove.mutateAsync, () => setToDelete(null));

  if (isLoading) return <Loading />;

  return (
    <>
      <TableShell
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        addLabel="საგნის დამატება"
        empty={!data?.length}
        emptyTitle="საგნები ჯერ არ არის"
        emptyText="საგანს ენიჭება პროექტი, მიმართულება, ეტაპები და საათები."
        head={
          <tr>
            <Th>დასახელება</Th>
            <Th>კოდი</Th>
            <Th>პროექტი</Th>
            <Th>მიმართულება</Th>
            <Th className="text-right">ეტაპი</Th>
            <Th className="text-right">სთ / ეტაპზე</Th>
            <Th className="text-right">ლექცია</Th>
            <Th className="text-right">ლექტორი</Th>
            <Th className="text-right">ჯგუფი</Th>
            <Th />
          </tr>
        }
      >
        {data?.map((c) => (
          <tr key={c.id} className="border-t border-line hover:bg-canvas/60">
            <Td className="font-medium text-ink">{c.name}</Td>
            <Td className="tabular text-ink-mute">{c.code}</Td>
            <Td>
              <span className="flex items-center gap-2">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: c.project.color ?? '#cfd4dc' }}
                />
                <span className="text-ink-soft">{c.project.name}</span>
              </span>
            </Td>
            <Td>
              <span className="flex items-center gap-2">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: c.direction.color ?? '#cfd4dc' }}
                />
                <span className="text-ink-soft">{c.direction.name}</span>
              </span>
            </Td>
            <Td className="tabular text-right">{c.stageCount}</Td>
            <Td className="tabular text-right">{c.hoursPerStage || '—'}</Td>
            <Td className="tabular text-right">
              {c.hoursPerStage ? (
                lessonsPerStage(c.hoursPerStage, c.lessonHours)
              ) : (
                <span className="text-warn" title="საათები არ არის მითითებული">
                  —
                </span>
              )}
            </Td>
            <Td className="tabular text-right">{c.lecturers?.length ?? 0}</Td>
            <Td className="tabular text-right">{c._count.groups}</Td>
            <Td className="text-right">
              <RowActions
                onEdit={() => {
                  setEditing(c);
                  setOpen(true);
                }}
                onDelete={() => setToDelete(c)}
              />
            </Td>
          </tr>
        ))}
      </TableShell>

      <CourseModal
        open={open}
        onClose={() => setOpen(false)}
        course={editing}
        directions={directions ?? []}
        projects={projects ?? []}
      />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && void confirmDelete(toDelete.id)}
        title="საგნის წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.name}</strong>. მოქმედება შეუქცევადია.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================
// საერთო ნაწილები
// ============================================================

function useDeleteHandler(fn: (id: string) => Promise<unknown>, onDone: () => void) {
  const toast = useToast();
  return async (id: string) => {
    try {
      await fn(id);
      toast.success('წაიშალა');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
      onDone();
    }
  };
}

/** „15 სექ 2026 — 30 ივნ 2027", „15 სექ 2026-დან", „30 ივნ 2027-მდე" ან „—" */
function periodLabel(start: string | null, end: string | null): string {
  if (start && end) return `${formatDate(start)} — ${formatDate(end)}`;
  if (start) return `${formatDate(start)}-დან`;
  if (end) return `${formatDate(end)}-მდე`;
  return '—';
}

function Loading() {
  return (
    <div className="flex justify-center py-16">
      <Spinner className="size-6" />
    </div>
  );
}

function TableShell({
  onAdd,
  addLabel,
  empty,
  emptyTitle,
  emptyText,
  head,
  children,
}: {
  onAdd: () => void;
  addLabel: string;
  empty: boolean;
  emptyTitle: string;
  emptyText: string;
  head: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs">
      <div className="flex justify-end border-b border-line px-4 py-3">
        <Button size="sm" icon={<Plus className="size-4" />} onClick={onAdd}>
          {addLabel}
        </Button>
      </div>

      {empty ? (
        <EmptyState title={emptyTitle} description={emptyText} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
              {head}
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={clsx('px-4 py-2.5 font-semibold', className)}>{children}</th>;
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={clsx('px-4 py-3 align-middle', className)}>{children}</td>;
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={clsx(
        'inline-block rounded px-2 py-0.5 text-[11px] font-medium',
        active ? 'bg-ok-soft text-ok' : 'bg-canvas text-ink-mute',
      )}
    >
      {active ? 'აქტიური' : 'გამორთული'}
    </span>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="flex justify-end gap-1">
      <button
        onClick={onEdit}
        aria-label="რედაქტირება"
        className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
      >
        <Pencil className="size-4" />
      </button>
      <button
        onClick={onDelete}
        aria-label="წაშლა"
        className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}
